import { useState, useMemo, useCallback } from "react";
import { Search, ChevronLeft, Lock, GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import type { SystemColumnToggle } from "./ListSettingsModal";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type FieldConfig = {
  fieldname: string;
  label: string;
  required?: boolean;
};

type ColumnSidebarPanelProps = {
  open: boolean;
  onClose: () => void;
  fields: FieldConfig[];
  visibleFieldOrder: string[];
  onToggleField: (fieldname: string, enabled: boolean) => void;
  onChangeVisibleFieldOrder: (order: string[]) => void;
  isLoading?: boolean;
  systemColumns?: SystemColumnToggle[];
  onSystemColumnToggle?: (id: string, visible: boolean) => void;
};

// ── Sortable field row ──────────────────────────────────────

function SortableFieldRow({
  field,
  isVisible,
  onToggle,
}: {
  field: FieldConfig;
  isVisible: boolean;
  onToggle: (fieldname: string, enabled: boolean) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.fieldname, disabled: !!field.required });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-1.5 h-9 px-2 rounded-xs hover:bg-neutral-50 dark:hover:bg-neutral-800/50",
        isDragging && "opacity-50 bg-neutral-100 dark:bg-neutral-800 shadow-sm z-10"
      )}
    >
      {field.required ? (
        <Lock className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
      ) : (
        <button
          type="button"
          className="p-0.5 -ml-0.5 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground shrink-0 touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      <span
        className={cn(
          "flex-1 text-sm truncate",
          isVisible ? "text-foreground" : "text-muted-foreground"
        )}
      >
        {field.label}
      </span>
      <Switch
        checked={isVisible}
        disabled={field.required}
        onCheckedChange={(checked) => {
          if (!field.required) onToggle(field.fieldname, checked);
        }}
        className="scale-75 origin-right"
      />
    </div>
  );
}

// ── Static field row (for hidden fields & search results) ───

function StaticFieldRow({
  field,
  isVisible,
  onToggle,
}: {
  field: FieldConfig;
  isVisible: boolean;
  onToggle: (fieldname: string, enabled: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 h-9 px-2 rounded-xs hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
      <div className="w-4 shrink-0" />
      <span
        className={cn(
          "flex-1 text-sm truncate",
          isVisible ? "text-foreground" : "text-muted-foreground"
        )}
      >
        {field.label}
      </span>
      <Switch
        checked={isVisible}
        disabled={field.required}
        onCheckedChange={(checked) => {
          if (!field.required) onToggle(field.fieldname, checked);
        }}
        className="scale-75 origin-right"
      />
    </div>
  );
}

// ── Main component ──────────────────────────────────────────

export function ColumnSidebarPanel({
  open,
  onClose,
  fields,
  visibleFieldOrder,
  onToggleField,
  onChangeVisibleFieldOrder,
  isLoading = false,
  systemColumns,
  onSystemColumnToggle,
}: ColumnSidebarPanelProps) {
  const [search, setSearch] = useState("");

  const visibleSet = useMemo(() => new Set(visibleFieldOrder), [visibleFieldOrder]);
  const fieldMap = useMemo(() => new Map(fields.map((f) => [f.fieldname, f])), [fields]);

  const isSearching = search.trim().length > 0;
  const searchLower = search.toLowerCase();

  // Visible fields — ordered by visibleFieldOrder, filtered by search
  const visibleFields = useMemo(() => {
    const ordered: FieldConfig[] = [];
    for (const fname of visibleFieldOrder) {
      const f = fieldMap.get(fname);
      if (!f) continue;
      if (isSearching && !f.label.toLowerCase().includes(searchLower)) continue;
      ordered.push(f);
    }
    return ordered;
  }, [visibleFieldOrder, fieldMap, isSearching, searchLower]);

  // Hidden fields — everything not in visibleFieldOrder, filtered by search
  const hiddenFields = useMemo(() => {
    return fields.filter((f) => {
      if (visibleSet.has(f.fieldname)) return false;
      if (isSearching && !f.label.toLowerCase().includes(searchLower)) return false;
      return true;
    });
  }, [fields, visibleSet, isSearching, searchLower]);

  const shownCount = useMemo(
    () => fields.filter((f) => visibleSet.has(f.fieldname)).length,
    [fields, visibleSet]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = visibleFieldOrder.indexOf(active.id as string);
      const newIndex = visibleFieldOrder.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      const newOrder = arrayMove(visibleFieldOrder, oldIndex, newIndex);
      onChangeVisibleFieldOrder(newOrder);
    },
    [visibleFieldOrder, onChangeVisibleFieldOrder]
  );

  const noResults = isSearching && visibleFields.length === 0 && hiddenFields.length === 0;

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/20 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed top-0 right-0 z-50 h-full w-[340px] bg-white dark:bg-neutral-900 border-l border-neutral-200 dark:border-neutral-700 shadow-xl",
          "flex flex-col",
          "transition-transform duration-200 ease-out",
          open ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-neutral-200 dark:border-neutral-700 shrink-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-xs hover:bg-neutral-100 dark:hover:bg-neutral-800 text-muted-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <h2 className="text-sm font-semibold text-foreground">Fields</h2>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            {shownCount} shown
          </div>
        </div>

        {/* Search */}
        <div className="px-3 py-2.5 border-b border-neutral-200 dark:border-neutral-700 shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search fields..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-8 pl-8 pr-3 text-sm bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xs outline-none focus:ring-1 focus:ring-neutral-400 dark:focus:ring-neutral-500 placeholder:text-muted-foreground/60"
            />
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {isLoading && fields.length === 0 && (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              Loading fields...
            </div>
          )}

          {/* Visible fields — sortable when not searching */}
          {visibleFields.length > 0 && (
            <>
              {!isSearching && (
                <div className="px-2 pb-1 text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">
                  Visible
                </div>
              )}
              {isSearching ? (
                <div className="space-y-0.5">
                  {visibleFields.map((field) => (
                    <StaticFieldRow
                      key={field.fieldname}
                      field={field}
                      isVisible
                      onToggle={onToggleField}
                    />
                  ))}
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={visibleFields.map((f) => f.fieldname)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-0.5">
                      {visibleFields.map((field) => (
                        <SortableFieldRow
                          key={field.fieldname}
                          field={field}
                          isVisible
                          onToggle={onToggleField}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </>
          )}

          {/* Hidden fields */}
          {hiddenFields.length > 0 && (
            <>
              {!isSearching && visibleFields.length > 0 && (
                <div className="my-2 border-t border-neutral-200 dark:border-neutral-700" />
              )}
              {!isSearching && (
                <div className="px-2 pb-1 text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">
                  Hidden
                </div>
              )}
              <div className="space-y-0.5">
                {hiddenFields.map((field) => (
                  <StaticFieldRow
                    key={field.fieldname}
                    field={field}
                    isVisible={false}
                    onToggle={onToggleField}
                  />
                ))}
              </div>
            </>
          )}

          {/* System columns */}
          {systemColumns && systemColumns.length > 0 && (
            <>
              <div className="my-2 border-t border-neutral-200 dark:border-neutral-700" />
              <div className="space-y-0.5">
                {systemColumns.map((col) => (
                  <div
                    key={col.id}
                    className="flex items-center gap-2.5 h-9 px-2 rounded-xs hover:bg-neutral-50 dark:hover:bg-neutral-800/50"
                  >
                    <span className="text-muted-foreground/60 shrink-0 w-3.5 flex items-center justify-center">
                      {col.icon}
                    </span>
                    <span className="flex-1 text-sm text-foreground truncate">
                      {col.label}
                    </span>
                    <Switch
                      checked={col.visible}
                      onCheckedChange={(checked) =>
                        onSystemColumnToggle?.(col.id, checked)
                      }
                      className="scale-75 origin-right"
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* No results */}
          {noResults && (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              No fields match &ldquo;{search}&rdquo;
            </div>
          )}
        </div>
      </div>
    </>
  );
}
